"use client"

/**
 * The currency a finance figure is denominated in (IBA-114).
 *
 * This replaces `useTenantSettingsStore((s) => s.settings.regional.baseCurrency)`, which read a WORKSPACE-wide
 * setting. Accounting belongs to branches (ADR-0022 §6): each branch keeps its own books, closes its own periods
 * and reports in its own currency. A workspace-wide currency was only ever *accidentally* right — it agreed with
 * every branch because every branch had been seeded from it. The moment two branches differ, a workspace-level
 * read renders the wrong symbol against real money, and nothing tells you.
 *
 * Two rules, in order:
 *
 *   1. **If the record carries a currency, use the record's.** A transaction, an account, a fund knows what it
 *      is denominated in. That is the only reading that stays correct in a list that spans branches.
 *   2. **Otherwise use the active branch's** — for empty states, form defaults, and totals that are branch-scoped
 *      by construction.
 *
 * Never fall back to a workspace setting. `useBranchCurrency` returns the active branch's currency, and the
 * branch summary already carries it (it rides along on the auth branch context), so this costs no extra fetch.
 */

import { Currency } from "@ibada/shared-types/finance"

import { useUserBranchAccessStore } from "@/features/auth/services/user-branch-access-store"

/** The currency to use when nothing else is known. Matches the backend's default for a new workspace. */
export const FALLBACK_CURRENCY = Currency.USD

/**
 * The active branch's accounting currency — what a branch-scoped figure is denominated in.
 *
 * Built from plain zustand stores ONLY: no Clerk, no Router. That is deliberate, and it is the same
 * provider-freedom the workspace settings store it replaces had. The obvious implementation
 * (`useFormBranchContext`) reaches `useUserBranchAccess` → Clerk's `useAuth`, which would drag an
 * auth-provider requirement into every component that merely wants to render a currency symbol — and would
 * break any component rendered outside a ClerkProvider. Knowing what currency a number is in must not require
 * being logged in through a specific identity provider.
 */
export function useBranchCurrency(): Currency {
  const accessibleBranches = useUserBranchAccessStore((state) => state.accessibleBranches)
  const primaryBranch = useUserBranchAccessStore((state) => state.primaryBranch)

  const activeBranch =
    primaryBranch ??
    accessibleBranches[0] ??
    null

  return (activeBranch?.baseCurrency as Currency | undefined) ?? FALLBACK_CURRENCY
}

/**
 * Prefer the RECORD's own currency, falling back to the branch's.
 *
 * Use this anywhere a row/detail is being rendered: it keeps a list correct even when it spans branches that
 * keep their books in different currencies, which the backend now permits.
 */
export function useRecordCurrency(recordCurrency: string | null | undefined): Currency {
  const branchCurrency = useBranchCurrency()
  return (recordCurrency as Currency | undefined) ?? branchCurrency
}
